<script lang="ts">
  import Spinner from '@/components/anim/spinner.svelte'
  import ForgotPasswordButton from '@/components/forgot-password-button.svelte'
  import TextField from '@/components/text-field.svelte'
  import { createFormContext } from '@/context/form'
  import Footer from '@/layouts/footer.svelte'
  import Main from '@/layouts/main.svelte'
  import { grantAccess, signIn } from '@/lib/api'
  import { sendAccountActionMessage } from '@/lib/native-messaging/account-action'
  import { Dialog, showDialog } from '@/lib/native-messaging/dialog'
  import {
    logComponentEvent,
    logGeneralEvent,
  } from '@/lib/native-messaging/log-event'
  import { dispatchWebViewAction } from '@/lib/native-messaging/web-view-action'
  import { redirect } from '@/lib/oauth/redirect'
  import { resolveURL } from '@/lib/utils/url'
  import { convertToken, redirectUri } from '@/params'
  import { SignInForm } from '@/schema'
  import { _ } from 'svelte-i18n'

  const isGuest = !!convertToken

  const params = new URLSearchParams(location.search)
  // appended in `/signup_or_signin` page
  const email = params.get('email')

  const { validate, allTouched, hasError } = createFormContext({
    schema: SignInForm,
    t: $_,
  })

  let isLoading = false

  async function submitGrantingAccess() {
    isLoading = true
    const [result, payload] = await grantAccess()
    isLoading = false
    if (result !== 'SUCCESS') {
      showDialog(Dialog.RetryDialog, (action) => {
        if (action === Dialog.RetryDialog.primaryBtnAction) {
          submitGrantingAccess()
        }
      })
      return
    }
    redirect(redirectUri, { ...Object.fromEntries(params), ...payload })
    if (isGuest) {
      sendAccountActionMessage({ action: 'guest_login_as_existed_user' })
    }
    dispatchWebViewAction({ action: 'end_session' })
  }

  async function handleSubmit(
    event: SubmitEvent & {
      currentTarget: EventTarget & HTMLFormElement
    },
  ) {
    const data = validate(new FormData(event.currentTarget))

    isLoading = true
    const [result, payload] = await signIn(data.password)
    isLoading = false
    switch (result) {
      case 'USER_SIGNED_IN':
        logGeneralEvent('seekrauth_login', {
          is_guest: isGuest.toString(),
        })
        submitGrantingAccess()
        break
      case 'EMAIL_NOT_VERIFIED':
        dispatchWebViewAction({
          action: 'push',
          url: resolveURL('/email_validation', {
            user_id: payload.id,
            from: '/signin',
          }).toString(),
          navBarTitle: $_('email_verification_title'),
          navBarVisible: true,
        })
        break
      case 'NETWORK_ERROR':
        showDialog(Dialog.InternetDisconnected)
        break
      case 'WRONG_PASSWORD':
        showDialog(Dialog.WrongPassword)
        break
      case 'UNHANDLED_ERROR':
        showDialog(Dialog.UnhandledError(payload.message))
        break
      case 'UNHANDLED_HTTP_ERROR':
        showDialog(Dialog.UnhandledHTTPError(payload))
        break
    }
  }
</script>

<form class="flex h-full flex-col" on:submit|preventDefault={handleSubmit}>
  <Main>
    <p class="text-content-secondary text-body-lg">
      {$_('signin_enter_password', {
        values: { email },
      })}
    </p>
    <TextField type="password" name="password" dataFieldName="login_password" />
    <div class="text-right text-body-md">
      <ForgotPasswordButton />
    </div>
  </Main>
  <Footer>
    <button
      type="submit"
      class="btn"
      disabled={isLoading || !$allTouched || $hasError}
      on:click={() => {
        logComponentEvent({
          type: 'btn',
          appAction: 'click',
          componentName: 'login',
        })
      }}
    >
      {#if isLoading}
        <Spinner />
      {/if}
      <span>{$_('btn_next')}</span>
    </button>
  </Footer>
</form>
